import React, { createContext, useContext, useReducer, useRef, useCallback } from "react";
import { createAsset, runJob, reanalyseAsset, reanalyseAssetWithFile } from "../services/dashboardService";
import { toFriendlyError } from "../../../utility/toFriendlyError";

// How long we keep the user on the foreground "analyzing" screen waiting for a
// fast result/error before handing the job off to the background. Past this,
// the analysis keeps running but the UI releases the user with a reassuring
// message ("generating, we'll notify you"). Must align with the visible
// countdown in AnalyzePage (COUNTDOWN_SECS).
const HANDOFF_MS = 45000;
const HANDOFF_MSG =
  "Your report is being generated — this can take a few minutes. You can keep working; we'll notify you on the dashboard when it's ready.";

// ── Retry helpers ─────────────────────────────────────────────────────────────
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Retries `fn` up to `maxAttempts` times with exponential back-off.
 * Only retries on network / 5xx / timeout errors, not on 4xx (client errors).
 * status 0 = network/timeout (fetch threw), 524 = Cloudflare timeout.
 */
const withRetry = async (fn, { maxAttempts = 3, delayMs = 2000 } = {}) => {
  let lastErr;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      // Don't retry client errors (4xx) except 408/429/503/524
      const status = err?.status ?? 0;
      const isClientError = status >= 400 && status < 500 && ![408, 429, 503].includes(status);
      if (isClientError) throw err;
      if (attempt < maxAttempts) {
        // Use longer delay for timeout-type errors (network, 524, 502, 503)
        const isTimeout = status === 0 || status === 524 || status === 502 || status === 503 || status === 504;
        const wait = isTimeout ? delayMs * attempt * 1.5 : delayMs * attempt;
        await sleep(wait);
      }
    }
  }
  throw lastErr;
};

const getApiMessage = (payload, fallback) =>
  payload?.error ||
  payload?.message ||
  payload?.data?.error ||
  payload?.data?.message ||
  fallback;

// The free trial ends in one of two ways, and both mean "ask for a card now"
// rather than "upgrade your plan".
const TRIAL_CODES = ['TRIAL_EXPIRED', 'TRIAL_RUNS_EXHAUSTED'];
const isTrialEnd = (payload) =>
  TRIAL_CODES.includes(payload?.code) || TRIAL_CODES.includes(payload?.data?.code);

const throwApiError = (payload, fallback) => {
  const ex = new Error(getApiMessage(payload, fallback));
  if (payload?.upgradePrompt || payload?.data?.upgradePrompt) ex.upgradePrompt = true;
  if (isTrialEnd(payload)) ex.trialEnded = true;
  ex.responseData = payload;
  throw ex;
};

// ── Constants ─────────────────────────────────────────────────────────────────
export const STEPS_COUNT = 4; // must match STEPS array in AnalyzePage

// ── Per-analysis shape ────────────────────────────────────────────────────────
const makeAnalysis = (id, softwareName) => ({
  id,
  status:          "running",
  step:            0,
  result:          null,
  softwareName,
  errorMsg:        "",
  errorTitle:      "",
  backgroundMsg:   "",
  upgradePrompt:   false,
  trialEnded:      false,
  alreadyAnalyzed: false,
  existingAssetId: null,
  startTime:       Date.now(),
});

// ── Actions ───────────────────────────────────────────────────────────────────
const A = {
  ADD:        "ADD",
  STEP:       "STEP",
  DONE:       "DONE",
  ERROR:      "ERROR",
  BACKGROUND: "BACKGROUND",
  UPGRADE:    "UPGRADE",
  ALREADY:    "ALREADY",
  REMOVE:     "REMOVE",
  SET_ACTIVE: "SET_ACTIVE",
};

// ── State ─────────────────────────────────────────────────────────────────────
// analyses: { [id]: analysisShape }
// activeId: id currently shown in AnalyzePage  (null = show the form)
const initialState = {
  analyses: {},
  activeId: null,
};

function reducer(state, { type, payload }) {
  switch (type) {
    case A.ADD:
      return {
        ...state,
        analyses: { ...state.analyses, [payload.id]: payload },
        activeId: payload.id, // new run becomes active immediately
      };

    case A.STEP: {
      const a = state.analyses[payload.id];
      if (!a) return state;
      return { ...state, analyses: { ...state.analyses, [payload.id]: { ...a, step: payload.step } } };
    }

    case A.DONE: {
      const a = state.analyses[payload.id];
      if (!a) return state;
      return {
        ...state,
        analyses: {
          ...state.analyses,
          [payload.id]: { ...a, status: "done", result: payload.result, step: STEPS_COUNT },
        },
      };
    }

    case A.ERROR: {
      const a = state.analyses[payload.id];
      if (!a) return state;
      return {
        ...state,
        analyses: {
          ...state.analyses,
          [payload.id]: { ...a, status: "error", errorMsg: payload.msg, errorTitle: payload.title || "" },
        },
      };
    }

    case A.BACKGROUND: {
      const a = state.analyses[payload.id];
      // Only hand off a still-running job — never override a done/errored one.
      if (!a || a.status !== "running") return state;
      return {
        ...state,
        analyses: {
          ...state.analyses,
          [payload.id]: { ...a, status: "background", backgroundMsg: payload.msg },
        },
      };
    }

    case A.UPGRADE: {
      const a = state.analyses[payload.id];
      if (!a) return state;
      return {
        ...state,
        analyses: {
          ...state.analyses,
          [payload.id]: {
            ...a,
            status: "error",
            upgradePrompt: !payload.trialEnded,
            trialEnded: !!payload.trialEnded,
            errorMsg: payload.msg,
          },
        },
      };
    }

    case A.ALREADY: {
      const a = state.analyses[payload.id];
      if (!a) return state;
      return {
        ...state,
        analyses: { ...state.analyses, [payload.id]: { ...a, status: "error", alreadyAnalyzed: true, existingAssetId: payload.existingAssetId ?? null } },
      };
    }

    case A.REMOVE: {
      const next = { ...state.analyses };
      delete next[payload.id];
      return {
        ...state,
        analyses: next,
        activeId: state.activeId === payload.id ? null : state.activeId,
      };
    }

    case A.SET_ACTIVE:
      return { ...state, activeId: payload };

    default:
      return state;
  }
}

// ── Context ───────────────────────────────────────────────────────────────────
const AnalysisContext = createContext(null);

// ── Id generator ──────────────────────────────────────────────────────────────
let _counter = 0;
const genId = () => `a_${Date.now()}_${++_counter}`;

// ── Provider ──────────────────────────────────────────────────────────────────
export const AnalysisProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Per-job abort/timer bookkeeping — keyed by analysis id
  const jobsRef = useRef({});

  const getJob = (id) => {
    if (!jobsRef.current[id]) jobsRef.current[id] = { aborted: false, timers: [] };
    return jobsRef.current[id];
  };

  const clearJob = (id) => {
    const job = jobsRef.current[id];
    if (job) {
      job.aborted = true;
      job.timers.forEach(clearTimeout);
    }
    delete jobsRef.current[id];
  };

  // After HANDOFF_MS, if the job is still running, release the user to the
  // background (the analysis keeps going). The timer is pushed into job.timers
  // so every terminal path (done/error/abort) already clears it; the reducer
  // also no-ops if the job is no longer "running", so a late fire is harmless.
  const startHandoffTimer = (id) => {
    const job = getJob(id);
    const t = setTimeout(() => {
      const j = jobsRef.current[id];
      if (!j || j.aborted) return;
      dispatch({ type: A.BACKGROUND, payload: { id, msg: HANDOFF_MSG } });
    }, HANDOFF_MS);
    job.timers.push(t);
  };

  const runFakeSteps = (id, waitUntil = 2) => {
    const job = getJob(id);
    dispatch({ type: A.STEP, payload: { id, step: 0 } });
    const schedule = [1500, 3200, 5200];
    return new Promise((resolve) => {
      schedule.forEach((delay, i) => {
        const idx = i + 1;
        const t = setTimeout(() => {
          if (job.aborted) return;
          dispatch({ type: A.STEP, payload: { id, step: idx } });
          if (idx >= waitUntil) resolve();
        }, delay);
        job.timers.push(t);
      });
    });
  };

  /**
   * Start a new analysis — runs entirely in the Provider so it survives navigation.
   * Dispatches ADD which sets activeId immediately, so AnalyzePage switches to
   * the analyzing view without any extra local state.
   */
  const runAnalysis = useCallback(async ({ formData, softwareName, transformFn }) => {
    const id = genId();
    getJob(id);

    dispatch({ type: A.ADD, payload: makeAnalysis(id, softwareName) });
    startHandoffTimer(id);

    try {
      const job = jobsRef.current[id];

      // 1. Asset creation + first two fake steps in parallel
      const [createRes] = await Promise.all([
        withRetry(() => createAsset(formData), { maxAttempts: 3, delayMs: 1500 }),
        runFakeSteps(id, 2),
      ]);
      if (job.aborted) return;

      if (createRes?.success === false) {
        throwApiError(createRes, "Analysis failed. Please try again.");
      }

      const createData = createRes?.data ?? createRes;

      if (createData?.analysis_status === "processing") {
        // Do not retry run-once: a second call can hit an empty queue and mask
        // the real failure from the first attempt.
        const jobRes = await withRetry(() => runJob(), { maxAttempts: 1, delayMs: 2500 });
        if (job.aborted) return;

        if (jobRes?.success === false) {
          throwApiError(jobRes, "Analysis failed. Please try again.");
        }

        const revisionReportJson = jobRes?.data?.revision?.report_json;
        if (!revisionReportJson) {
          throwApiError(jobRes, "Analysis failed. Please try again.");
        }

        job.timers.forEach(clearTimeout);
        job.timers = [];
        dispatch({ type: A.STEP, payload: { id, step: 3 } });

        const transformed = transformFn(revisionReportJson, jobRes?.data ?? {});

        const t1 = setTimeout(() => {
          if (job.aborted) return;
          dispatch({ type: A.STEP, payload: { id, step: STEPS_COUNT } });
          const t2 = setTimeout(() => {
            if (job.aborted) return;
            dispatch({ type: A.DONE, payload: { id, result: transformed } });
          }, 800);
          job.timers.push(t2);
        }, 1200);
        job.timers.push(t1);

      } else if (
        createData?.analysis_status === "complete" ||
        createData?.source === "cache"
      ) {
        // Handles both a freshly-completed asset and the 48h cache response
        // Cache shape: { source: "cache", report: { report_metadata, privacy_analysis, ... } }
        // Complete shape: { analysis_status: "complete", report: { ... } }
        job.timers.forEach(clearTimeout);
        job.timers = [];
        dispatch({ type: A.STEP, payload: { id, step: 3 } });

        const reportJson = createData?.report;
        if (!reportJson) {
          throwApiError(createRes, "Analysis failed. Please try again.");
        }

        const transformed = transformFn(reportJson, createData ?? {});

        const t1 = setTimeout(() => {
          if (job.aborted) return;
          dispatch({ type: A.STEP, payload: { id, step: STEPS_COUNT } });
          const t2 = setTimeout(() => {
            if (job.aborted) return;
            dispatch({ type: A.DONE, payload: { id, result: transformed } });
          }, 800);
          job.timers.push(t2);
        }, 1200);
        job.timers.push(t1);

      } else {
        job.timers.forEach(clearTimeout);
        job.timers = [];
        throw new Error(getApiMessage(createRes, "Unexpected status from server. Please try again."));
      }
    } catch (err) {
      const job = jobsRef.current[id];
      if (!job || job.aborted) return;
      job.timers.forEach(clearTimeout);
      job.timers = [];

      const msg = err.message || "";
      const isAlreadyAnalyzed =
        msg.toLowerCase().includes("already analyzed") ||
        msg.toLowerCase().includes("already exist") ||
        msg.toLowerCase().includes("asset_already");

      if (isAlreadyAnalyzed) {
        const existingAssetId = err.responseData?.data?.existingAsset?.id ?? null;
        dispatch({ type: A.ALREADY, payload: { id, existingAssetId } });
      } else if (err.upgradePrompt || err.trialEnded) {
        dispatch({ type: A.UPGRADE, payload: { id, msg, trialEnded: !!err.trialEnded } });
      } else {
        const friendly = toFriendlyError(err);
        dispatch({ type: A.ERROR, payload: { id, msg: friendly.message, title: friendly.title } });
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Re-analyse an existing asset via GET /api/v1/assets/{id}/reanalyse.
   * Creates a fresh analysis entry so the analysing animation plays,
   * then resolves to the existing report (no extra credit consumed).
   */
  const runReanalysis = useCallback(async ({ existingAssetId, softwareName, transformFn, formData = null }) => {
    const id = genId();
    getJob(id);
    dispatch({ type: A.ADD, payload: makeAnalysis(id, softwareName) });
    startHandoffTimer(id);

    try {
      const job = jobsRef.current[id];

      // When a new policy document is supplied, upload it as a new version and
      // re-analyse against it; otherwise re-run against the last stored document.
      const reanalyseCall = formData
        ? reanalyseAssetWithFile(existingAssetId, formData)
        : reanalyseAsset(existingAssetId);

      const [reanalysRes] = await Promise.all([
        reanalyseCall,
        runFakeSteps(id, 2),
      ]);
      if (job.aborted) return;

      if (reanalysRes?.success === false) {
        throwApiError(reanalysRes, "Re-analysis failed. Please try again.");
      }

      job.timers.forEach(clearTimeout);
      job.timers = [];
      dispatch({ type: A.STEP, payload: { id, step: 3 } });

      // Response shape: { success, data: { source, report: { report_metadata, privacy_analysis, ... } } }
      const rawData    = reanalysRes?.data ?? reanalysRes;
      const reportJson = rawData?.report            // reanalyse endpoint: data.report IS the report json
                      ?? rawData?.revision?.report_json  // fallback: standard revision shape
                      ?? rawData?.report_json
                      ?? rawData;

      if (!reportJson || !reportJson?.privacy_analysis) {
        throwApiError(reanalysRes, "Re-analysis failed. Please try again.");
      }

      const transformed = transformFn(reportJson, rawData ?? {});

      const t1 = setTimeout(() => {
        if (job.aborted) return;
        dispatch({ type: A.STEP, payload: { id, step: STEPS_COUNT } });
        const t2 = setTimeout(() => {
          if (job.aborted) return;
          dispatch({ type: A.DONE, payload: { id, result: transformed } });
        }, 800);
        job.timers.push(t2);
      }, 1200);
      job.timers.push(t1);

    } catch (err) {
      const job = jobsRef.current[id];
      if (!job || job.aborted) return;
      job.timers.forEach(clearTimeout);
      job.timers = [];
      const friendly = toFriendlyError(err);
      dispatch({ type: A.ERROR, payload: { id, msg: friendly.message, title: friendly.title } });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Abort a running analysis and remove it from the map. */
  const cancelAnalysis = useCallback((id) => {
    clearJob(id);
    dispatch({ type: A.REMOVE, payload: { id } });
  }, []);

  /** Remove a completed/errored analysis (called when closing result modals). */
  const resetAnalysis = useCallback((id) => {
    if (id) {
      clearJob(id);
      dispatch({ type: A.REMOVE, payload: { id } });
    }
  }, []);

  /** Set which analysis AnalyzePage is showing. Pass null to reveal the form. */
  const setActiveId = useCallback((id) => {
    dispatch({ type: A.SET_ACTIVE, payload: id ?? null });
  }, []);

  /**
   * Called by chips "Open / View progress" buttons.
   * Sets the analysis as active so AnalyzePage shows its progress view.
   */
  const showProgress = useCallback((id) => {
    dispatch({ type: A.SET_ACTIVE, payload: id });
  }, []);

  return (
    <AnalysisContext.Provider
      value={{ state, runAnalysis, runReanalysis, cancelAnalysis, resetAnalysis, setActiveId, showProgress }}
    >
      {children}
    </AnalysisContext.Provider>
  );
};

// ── Hook ──────────────────────────────────────────────────────────────────────
export const useAnalysis = () => {
  const ctx = useContext(AnalysisContext);
  if (!ctx) throw new Error("useAnalysis must be used inside <AnalysisProvider>");
  return ctx;
};
